import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import * as THREE from 'three'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MapboxScene, type MapboxSceneRefs } from '@/components/3d'
import { AircraftOverlay, AircraftLabelsOverlay, AircraftSpritesOverlay, MapLabelsOverlay } from '@/components/hangar-view'
import { MoveBar } from '@/components/hangar-view/MoveBar'
import { AircraftFullModal } from '@/components/hangar-view/AircraftFullModal'
import { EditFormContainer } from '@/components/hangar-view/edit-forms'
import { RampViewConfigPanel } from '@/components/ramp-view/RampViewConfigPanel'
import { CollisionDebugPanel } from '@/components/debug/CollisionDebugPanel'
import { AddPlaneWizard, useAddPlaneWizard } from '@/components/ramp-wizard'
import { useAddPlaneWizardStore } from '@/stores/addPlaneWizardStore'
import { useAppContext, useAircraftDragInteraction, useMapBuildings, useMapAllocations, useMapPlacementMode, useInitialViewSettings, useLocationDisplay } from '@/hooks'
import { useCollisionDetection } from '@/hooks/useCollisionDetection'
import { useMapLabelPositions } from '@/hooks/useMapLabelPositions'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { useRampViewConfigStore } from '@/stores/rampViewConfigStore'
import { useCollisionStore } from '@/stores/collisionStore'
import { useRampFilterStore } from '@/stores/rampFilterStore'
import { useRampPanelStore } from '@/stores/rampPanelStore'
import { useCinemaStore } from '@/stores/cinemaStore'
import { CinemaToggleButton } from '@/components/cinema'
import { LocationZoomLabel } from '@/components/overlay-ui'
import { FlythroughButton, FlythroughPanel, useFlythroughPlayback } from '@/features/flythrough'
import { updateAllocationPosition } from '@/lib/api/allocations'
import { detectHangarForPosition } from '@/lib/utils/hangar-detection'
import { calculateUnitCenterPosition } from '@/lib/utils/unit-position'
import { CommandIsland, ActionIsland, StatusIsland, FilterChipsIsland, SidebarToggle, ViewPresetsIsland, DragDropToggleIsland } from '@/components/islands'
import { PriceCalculatorModal } from '@/components/price-calculator'
import { AircraftListPanel } from '@/components/panels/AircraftListPanel'
import { SpacesListPanel } from '@/components/panels/SpacesListPanel'
import { QuickFitPanel } from '@/components/panels/QuickFitPanel'
import { ArrivalsPanel } from '@/components/panels/ArrivalsPanel'
import type { PlacedAircraft3D } from '@/services/3d'
import type { HangarCollisionConfig, UnitCollisionConfig, EdgeCollisionConfig, WallSegment, DoorCollisionSegment, ArchCollisionConfig } from '@/types/collision'
import type { DoorSpec, EdgeSide } from '@/lib/templates/types'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { FEET_TO_METERS } from '@/constants/units'
import { debounce } from '@/services/collision'
import { ENABLE_COLLISION_DEBUG } from '@/constants/collision'
import { Autostacker5DDemo, FlightBridgeSyncButton, DemoResetButton } from '@/demo/5d-autostacker/Autostacker5DDemo'
import { useDemoMode } from '@/demo/5d-autostacker/hooks/useDemoMode'
import { DEMO_CONFIG } from '@/demo/5d-autostacker/data/demoConfig'

export function RampPage() {
  // Get location from URL context
  const { locationId, locationName } = useAppContext()

  // Name + ICAO for the zoomed-out facility label
  const locationDisplay = useLocationDisplay()

  // Fetch areas for hangar/unit detection
  const { buildings: areas, center: mapCenter, viewSettings: locationViewSettings } = useMapBuildings(locationId)

  // Merge location view settings with default view preset (blocks map init until both resolve)
  const initialViewSettings = useInitialViewSettings(locationId, locationViewSettings)

  // Scene refs for interaction
  const [sceneRefs, setSceneRefs] = useState<MapboxSceneRefs | null>(null)
  const [aircraftData, setAircraftData] = useState<PlacedAircraft3D[]>([])

  // Price calculator modal
  const [priceCalcOpen, setPriceCalcOpen] = useState(false)

  // 5D Autostacker demo — only active at KOPF
  const isKopf = locationId === DEMO_CONFIG.locationId
  const demo = useDemoMode()


  // Collision state with stable key for React dependency detection
  const collidingAircraftIds = useCollisionStore((state) => state.collidingAircraftIds)
  // Create stable string key for React dependency - Sets don't trigger re-renders on content change
  const collisionKey = useMemo(
    () => Array.from(collidingAircraftIds).sort().join(','),
    [collidingAircraftIds]
  )

  // Helper: Calculate solid wall segments (wall minus door openings)
  const calculateSolidSegments = (wallLength: number, doors: DoorSpec[]): WallSegment[] => {
    if (!doors || doors.length === 0) {
      // No doors - entire wall is solid
      return [{ start_ft: 0, end_ft: wallLength }]
    }

    // Sort doors by offset
    const sortedDoors = [...doors].sort((a, b) => a.offset_ft - b.offset_ft)
    const segments: WallSegment[] = []
    let currentPos = 0

    for (const door of sortedDoors) {
      // Add solid segment before this door (if any)
      if (door.offset_ft > currentPos) {
        segments.push({ start_ft: currentPos, end_ft: door.offset_ft })
      }
      // Move past the door
      currentPos = door.offset_ft + door.width_ft
    }

    // Add final solid segment after last door (if any)
    if (currentPos < wallLength) {
      segments.push({ start_ft: currentPos, end_ft: wallLength })
    }

    return segments
  }

  // Helper: Get wall length based on edge side and unit dimensions
  const getWallLength = (side: EdgeSide, width: number, depth: number): number => {
    return (side === 'front' || side === 'back') ? width : depth
  }

  // Helper: Calculate door collision segments from door openings
  const calculateDoorSegments = (doors: DoorSpec[]): DoorCollisionSegment[] => {
    if (!doors || doors.length === 0) return []
    return doors.map(door => ({
      start_ft: door.offset_ft,
      end_ft: door.offset_ft + door.width_ft,
      height_ft: door.height_ft,
      doorType: door.type,
    }))
  }

  // Build UnitCollisionConfig from areas with generatedUnits
  const unitCollisionConfigs = useMemo<UnitCollisionConfig[]>(() => {
    // Validate mapCenter has valid coordinates (not NaN)
    if (!areas || !mapCenter || isNaN(mapCenter[0]) || isNaN(mapCenter[1])) return []

    // Debug: Log what areas we have
    if (ENABLE_COLLISION_DEBUG) {
      console.log('🏗️ Areas for collision:', areas.map(a => ({
        id: a.id,
        name: a.name,
        hasGeneratedUnits: !!a.generatedUnits,
        generatedUnitsCount: a.generatedUnits?.length || 0,
        typeConfig: a.typeConfig,
      })))
    }
    const configs: UnitCollisionConfig[] = []

    for (const area of areas) {
      // Skip areas without generated units
      if (!area.generatedUnits || area.generatedUnits.length === 0) continue

      // Skip outdoor areas (tiedown/ramp) — no walls to collide with
      if (area.templateType === 'outdoor_rectangular') continue

      const buildingRotation = area.rotation || 0
      const wallThickness = (area.typeConfig?.wall_thickness_ft as number) ?? 0.5

      for (const unit of area.generatedUnits) {
        // Calculate unit center using the same transform pipeline as rendering:
        // 1. Center offset_x (left edge → center), negate offset_y (DB north → scene -Z)
        // 2. Center around building origin (-width/2, +depth/2 for double_row only)
        // 3. Apply building rotation
        // 4. Add building world position
        const unitPos = calculateUnitCenterPosition(
          { offset_x: unit.offset_x, offset_y: unit.offset_y, interior_width: unit.interior_width },
          { position: area.position, rotation: buildingRotation, width: area.width, depth: area.depth },
          area.templateType === 'double_row'
        )
        // Convert feet → meters for collision config
        const unitMetersX = unitPos.x * FEET_TO_METERS
        const unitMetersZ = unitPos.z * FEET_TO_METERS

        // Build edge collision configs from unit.edges
        const edgeConfigs: EdgeCollisionConfig[] = []
        const edges = unit.edges || []

        for (const edge of edges) {
          // Skip internal edges (open bays) - no collision
          if (edge.type === 'internal') continue

          const wallLength = getWallLength(edge.side, unit.interior_width, unit.interior_depth)
          const doorOpenings = edge.openings || []
          const solidSegments = calculateSolidSegments(wallLength, doorOpenings)

          // Calculate door segments for collision detection
          const doorSegments = calculateDoorSegments(doorOpenings)

          edgeConfigs.push({
            side: edge.side,
            type: edge.type,
            length_ft: wallLength,
            height_ft: unit.interior_height,
            solidSegments,
            doorOpenings,
            doorSegments,
          })
        }

        // For arch hangars, build arch-specific collision config from type_config
        let archConfig: ArchCollisionConfig | undefined
        if (area.templateType === 'arch_hangar' && area.typeConfig) {
          const tc = area.typeConfig as Record<string, unknown>
          const frontDoor = tc.front_door as { enabled?: boolean; width_ft?: number; height_ft?: number; type?: string } | undefined
          const backDoor = tc.back_door as { enabled?: boolean; width_ft?: number; height_ft?: number; type?: string } | undefined

          // Extract door specs from computed edge openings (already in DoorSpec format)
          const frontEdge = edgeConfigs.find(e => e.side === 'front')
          const backEdge = edgeConfigs.find(e => e.side === 'back')

          archConfig = {
            span_ft: (tc.span_ft as number) || unit.interior_width,
            length_ft: (tc.length_ft as number) || unit.interior_depth,
            crown_height_ft: (tc.crown_height_ft as number) || 40,
            knee_wall_height_ft: (tc.knee_wall_height_ft as number) || 0,
            arch_profile: (tc.arch_profile as 'semicircular' | 'parabolic') || 'semicircular',
            arch_segments: (tc.arch_segments as number) || 24,
            wall_thickness_ft: (tc.wall_thickness_ft as number) || wallThickness,
            frontDoors: frontDoor?.enabled ? frontEdge?.doorOpenings : undefined,
            backDoors: backDoor?.enabled ? backEdge?.doorOpenings : undefined,
          }
        }

        configs.push({
          unitId: unit.code,
          areaId: area.id,
          position: [unitMetersX, 0, unitMetersZ], // meters
          dimensions: [unit.interior_width, unit.interior_depth, unit.interior_height], // feet
          wallThickness,
          rotation: buildingRotation + ((unit.unit_rotation || 0) * 180) / Math.PI,
          edges: edgeConfigs,
          archConfig,
        })
      }
    }

    if (ENABLE_COLLISION_DEBUG && configs.length > 0) {
      console.log('🏗️ Unit collision configs:', configs.map(c => ({
        unitId: c.unitId,
        areaId: c.areaId,
        position: c.position.map(p => p.toFixed(2)),
        dimensions: c.dimensions,
        edgeCount: c.edges.length,
        edges: c.edges.map(e => ({
          side: e.side,
          type: e.type,
          doors: e.doorOpenings.length,
          solidSegments: e.solidSegments.length,
        })),
      })))
    }

    return configs
  }, [areas, mapCenter])

  // Legacy hangarConfigs for backwards compatibility (empty for now)
  // TODO: Remove once collision service is updated to use unitCollisionConfigs
  const hangarConfigs = useMemo<HangarCollisionConfig[]>(() => [], [])

  // Build sceneObjects Map for collision detection
  const sceneObjectsMap = useMemo(() => {
    if (!sceneRefs?.sceneObjects?.scene) return new Map<string, THREE.Object3D>()

    const map = new Map<string, THREE.Object3D>()
    for (const aircraft of aircraftData) {
      const obj = sceneRefs.sceneObjects.scene.getObjectByName(aircraft.id)
      if (obj) {
        map.set(aircraft.id, obj)
      }
    }
    return map
  }, [sceneRefs?.sceneObjects?.scene, aircraftData])

  // Fetch enriched business data for the floating card
  // (TanStack Query deduplicates - same query key as MapboxScene's internal call)
  const { enrichedData } = useMapAllocations(locationId)

  // Initialize collision detection
  const {
    checkSingleAircraft,
          } = useCollisionDetection({
    enabled: true,
    hangars: hangarConfigs,
    units: unitCollisionConfigs,
    aircraft: aircraftData,
    sceneObjects: sceneObjectsMap,
    onCollisionChange: (collisions) => {
      if (collisions.size > 0) {
        console.log(`🚨 Collision detected: ${Array.from(collisions).join(', ')}`)
      }
    },
  })

  // Store latest checkSingleAircraft in a ref to avoid stale closure in debounced function
  const checkSingleAircraftRef = useRef(checkSingleAircraft)
  useEffect(() => {
    checkSingleAircraftRef.current = checkSingleAircraft
  }, [checkSingleAircraft])

  // Create debounced collision check for drag performance
  // Uses ref to always call latest checkSingleAircraft (avoids stale closure bug)
  const debouncedCollisionCheckRef = useRef(
    debounce((allocationId: string) => {
      checkSingleAircraftRef.current(allocationId)
      // Trigger repaint to update collision visual feedback
      sceneRefs?.map?.triggerRepaint()
    }, 100)
  )

  // Collision check callback for drag
  const handleCollisionCheck = useCallback(
    (allocationId: string) => {
      if (ENABLE_COLLISION_DEBUG) {
        console.log('📞 handleCollisionCheck called:', allocationId)
      }
      debouncedCollisionCheckRef.current(allocationId)
    },
    []
  )

  // Update visual feedback when collision state changes
  // Uses collisionKey (string) as trigger - collidingAircraftIds is passed but NOT in deps
  // to prevent double-firing (collisionKey is derived from collidingAircraftIds)
  useEffect(() => {
    if (!sceneRefs?.layerService) return

    if (ENABLE_COLLISION_DEBUG) {
      console.log('🎨 Collision state changed:', {
        collisionKey,
        collidingIds: Array.from(collidingAircraftIds),
      })
    }

    // Update aircraft materials based on collision state
    sceneRefs.layerService.updateAircraftCollisionState(collidingAircraftIds)
    sceneRefs.map?.triggerRepaint()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collisionKey, sceneRefs?.layerService, sceneRefs?.map])

  // Store actions
  const {
    selectedAllocationId,
    revertAllocation,
    markClean,
    getEditedAllocation,
      } = useAircraftEditorStore()

  const isDragging = useAircraftEditorStore((s) => s.isDragging)
  const isRotating = useAircraftEditorStore((s) => s.isRotating)

  // Ramp view config for label positioning
  const showAreaLabels = useRampViewConfigStore((s) => s.showAreaLabels)
  const showUnitLabels = useRampViewConfigStore((s) => s.showUnitLabels)
  const showLeaderLines = useRampViewConfigStore((s) => s.showLeaderLines)
  const showSpritePreviews = useRampViewConfigStore((s) => s.showSpritePreviews)

  // Shared label position loop — single map.on('move') listener + RAF for all overlays
  const { onFrame } = useMapLabelPositions({
    map: sceneRefs?.map ?? null,
    mapCenter,
    aircraftData,
    buildings: areas,
    showAreaLabels,
    showUnitLabels,
    showLeaderLines,
    selectedAllocationId,
  })

  // Wizard state and actions
  const { openWizard } = useAddPlaneWizardStore()

  // Wizard hook with submission logic
  const {
    submit: submitWizard,
    isPlacementModeActive,
  } = useAddPlaneWizard({
    locationId,
    onSuccess: () => {
      // Wizard submission successful - aircraft will be added
      // The query cache will be invalidated automatically
    },
  })

  // Detect incoming wizard from non-ramp pages (transient/lease)
  // When they navigated here for placement, auto-activate placement mode
  // Depends on sceneRefs so we wait for the 3D scene to fully initialize
  useEffect(() => {
    if (!sceneRefs?.map) return // Wait for map to be ready

    const state = useAddPlaneWizardStore.getState()
    if (
      state.wizardContext.source !== 'ramp' &&
      state.locationMethod === 'placement' &&
      !state.isPlacementModeActive
    ) {
      // Close the dialog (if open) but keep wizard data, then activate placement mode
      if (state.isOpen) {
        state.closeWizard()
      }
      state.activatePlacementMode()
    }
  }, [sceneRefs?.map])

  // Auto-minimize islands during drag/rotate/placement
  const isMinimized = isDragging || isRotating || isPlacementModeActive

  // Cinema mode — blanks all floating chrome for a clean screen capture.
  const cinemaActive = useCinemaStore((state) => state.active)

  // Camera flythrough. Scoped per location so tours don't leak between FBOs.
  const [flythroughOpen, setFlythroughOpen] = useState(false)
  const flythroughScope = `ramp:${locationId ?? 'none'}`
  useFlythroughPlayback({
    map: sceneRefs?.map ?? null,
    layerService: sceneRefs?.layerService ?? null,
    scope: flythroughScope,
  })

  // Filter store
  const { displayMode, highlightedAircraftIds, matchesFilters } = useRampFilterStore()
  const activePresets = useRampFilterStore((s) => s.activePresets)

  // Compute filtered/highlighted data for 3D scene
  const filterActive = activePresets.size > 0
  const highlightActive = highlightedAircraftIds.size > 0

  // Arrivals count for the ActionIsland badge
  const arrivalsCount = useMemo(() => {
    let count = 0
    const todayEnd = new Date()
    todayEnd.setHours(23, 59, 59, 999)
    for (const data of enrichedData.values()) {
      const { presence, startsAt, expectedReturnAt } = data.allocation
      if (presence === 'expected' && startsAt && new Date(startsAt) <= todayEnd) count++
      if (presence === 'away' && expectedReturnAt) count++
    }
    return count
  }, [enrichedData])

  // Build the set of aircraft IDs that should be highlighted (dimmed) in the 3D scene
  const highlightedIdsForScene = useMemo<Set<string>>(() => {
    // If search highlights are active, those take priority
    if (highlightActive) return highlightedAircraftIds

    // If filter presets are active in highlight mode, compute matching IDs
    if (filterActive && displayMode === 'highlight') {
      const matchingIds = new Set<string>()
      for (const [id, data] of enrichedData) {
        if (matchesFilters(data)) {
          matchingIds.add(id)
        }
      }
      return matchingIds
    }

    // No highlights active
    return new Set()
  }, [highlightActive, highlightedAircraftIds, filterActive, displayMode, enrichedData, matchesFilters])

  // Update 3D highlight state when highlighted IDs change
  const highlightKey = useMemo(() => Array.from(highlightedIdsForScene).sort().join(','), [highlightedIdsForScene])
  useEffect(() => {
    if (!sceneRefs?.layerService) return
    sceneRefs.layerService.updateAircraftHighlightState(highlightedIdsForScene)
    sceneRefs.map?.triggerRepaint()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightKey, sceneRefs?.layerService, sceneRefs?.map])

  // Filter aircraft data for rendering (only in filter mode)
  const filteredAircraftData = useMemo(() => {
    if (!filterActive || displayMode !== 'filter') return aircraftData

    return aircraftData.filter((aircraft) => {
      const data = enrichedData.get(aircraft.id)
      if (!data) return true // Keep aircraft without enriched data
      return matchesFilters(data)
    })
  }, [aircraftData, filterActive, displayMode, enrichedData, matchesFilters])

  // Compute total unit count for StatusIsland
  const totalUnits = useMemo(() => {
    let count = 0
    for (const area of areas) {
      count += area.generatedUnits?.length || 0
    }
    return count
  }, [areas])

  // Derive area/unit info for SpacesListPanel
  const spacesAreas = useMemo(() => {
    return areas.map((area) => ({
      id: area.id,
      name: area.name,
      areaCode: (area.templateType || '').replace(/_/g, ' '),
      type: area.templateType || 'unknown',
    }))
  }, [areas])

  const spacesUnits = useMemo(() => {
    const units: { id: string; unitCode: string; areaId: string; type: string | null }[] = []
    for (const area of areas) {
      if (!area.generatedUnits) continue
      for (const unit of area.generatedUnits) {
        units.push({
          id: unit.id || unit.code,
          unitCode: unit.code,
          areaId: area.id,
          type: unit.type || null,
        })
      }
    }
    return units
  }, [areas])

  // Placement mode hook - handles ghost aircraft cursor tracking
  // Pass map directly (like useAircraftDragInteraction) for proper event handling
  useMapPlacementMode({
    map: sceneRefs?.map ?? null,
    sceneObjects: sceneRefs?.sceneObjects ?? null,
    sceneCenter: mapCenter ?? null,
    buildings: areas,
    onPlacementConfirm: () => {
      // When placement is confirmed, submit the wizard
      submitWizard()
    },
    onPlacementCancel: () => {
      // Cancellation already reopens the wizard via store action
    },
  })

  // Handler for opening the add plane wizard
  const handleAddPlane = useCallback(() => {
    openWizard({ source: 'ramp', locationId })
  }, [openWizard, locationId])

  // Focus camera on a specific aircraft (used by search, list panel)
  const handleFocusAircraft = useCallback(
    (allocationId: string) => {
      if (!sceneRefs?.map || !mapCenter) return

      // Find aircraft position from aircraft data
      const aircraft3d = aircraftData.find((a) => a.id === allocationId)

      if (aircraft3d && sceneRefs.map) {
        // Convert aircraft position (feet) to lng/lat
        const lngLat = CoordinateService.scenePositionToLngLat(
          aircraft3d.position,
          mapCenter
        )

        // Select the aircraft in editor store (this triggers the card to appear)
        useAircraftEditorStore.getState().selectAllocation(allocationId)

        // Fly to aircraft position on map with zoom
        sceneRefs.map.flyTo({
          center: lngLat,
          zoom: 19.5,
          pitch: 55,
          duration: 1200,
        })

        sceneRefs.map.triggerRepaint()
      }
    },
    [sceneRefs?.map, mapCenter, aircraftData]
  )

  // Highlight aircraft from search (dim all others)
  const handleHighlightAircraft = useCallback(
    (allocationIds: string[]) => {
      useRampFilterStore.getState().setHighlightedAircraft(new Set(allocationIds))
    },
    []
  )

  // Focus camera on an area/building
  const handleFocusArea = useCallback(
    (areaId: string) => {
      if (!sceneRefs?.map) return

      // Find area and fly to it
      const area = areas.find((a) => a.id === areaId)
      if (area) {
        sceneRefs.map.flyTo({
          zoom: 18.5,
          pitch: 55,
          duration: 1200,
        })
        sceneRefs.map.triggerRepaint()
      }
    },
    [sceneRefs?.map, areas]
  )

  // Toggle filters from action island
  const handleToggleFilters = useCallback(() => {
    const store = useRampFilterStore.getState()
    if (store.hasActiveFilters()) {
      store.clearAllPresets()
    } else {
      // Toggle the first preset as a quick action
      store.togglePreset('based')
    }
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

      // These are BARE-letter shortcuts. Without this guard they also fire for
      // chorded ones — Ctrl+Shift+H would open the Spaces panel on its way to
      // toggling cinema mode.
      if (e.ctrlKey || e.metaKey || e.altKey) return

      // In cinema mode they would open panels the user can't see (the chrome
      // wrapper is transparent, not unmounted).
      if (useCinemaStore.getState().active) return

      switch (e.key.toLowerCase()) {
        case 'l':
          useRampPanelStore.getState().togglePanel('aircraft-list')
          break
        case 'h':
          useRampPanelStore.getState().togglePanel('spaces-list')
          break
        case 'f':
          handleToggleFilters()
          break
        case 'a':
          useRampPanelStore.getState().togglePanel('arrivals')
          break
        case 'q':
          useRampPanelStore.getState().togglePanel('quick-fit')
          break
        case 'p':
          setPriceCalcOpen((prev) => !prev)
          break
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleToggleFilters])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (allocationId: string) => {
      const edited = getEditedAllocation(allocationId)
      if (!edited) throw new Error('No edited allocation found')

      // Auto-detect which hangar/area contains this aircraft
      const detection = detectHangarForPosition(edited.currentPosition, areas)

      await updateAllocationPosition(
        allocationId,
        edited.currentPosition,
        edited.currentRotation,
        detection.areaId,
        detection.unitId,
        detection.positionType
      )
    },
    onSuccess: (_, allocationId) => {
      markClean(allocationId)
      useAircraftEditorStore.getState().setDragging(false)
      // Do NOT invalidate query - 3D scene is already correct after drag
      toast.success('Aircraft position saved')
    },
    onError: (error) => {
      toast.error('Failed to save position: ' + (error as Error).message)
    },
  })

  // Handle map ready
  const handleMapReady = useCallback(
    (refs: MapboxSceneRefs, aircraft: PlacedAircraft3D[]) => {
      setSceneRefs(refs)
      setAircraftData(aircraft)
    },
    []
  )

  // Handle aircraft move (update 3D model position)
  const handleAircraftMove = useCallback(
    (_allocationId: string, _position: [number, number, number]) => {
      // Position update is already handled in the drag interaction hook
      // This callback can be used for additional logic if needed
      sceneRefs?.layerService?.getSceneObjects()?.scene?.traverse(() => {})
    },
    [sceneRefs]
  )

  // Handle aircraft rotate (update 3D model rotation)
  const handleAircraftRotate = useCallback(
    (_allocationId: string, _rotation: number) => {
      // Rotation update is already handled in the drag interaction hook
    },
    []
  )

  // Use drag interaction hook (disabled during placement mode to avoid conflicts)
  useAircraftDragInteraction({
    map: sceneRefs?.map ?? null,
    sceneObjects: sceneRefs?.sceneObjects ?? null,
    enabled: !isPlacementModeActive,
    aircraftData,
    buildings: areas,
    onAircraftMove: handleAircraftMove,
    onAircraftRotate: handleAircraftRotate,
    onCollisionCheck: handleCollisionCheck,
  })

  // Handle save button click
  const handleSaveClick = useCallback(
    (allocationId: string) => {
      saveMutation.mutate(allocationId)
    },
    [saveMutation]
  )

  // Handle revert button click
  const handleRevertClick = useCallback(
    (allocationId: string) => {
      const reverted = revertAllocation(allocationId)
      if (reverted && sceneRefs?.sceneObjects?.scene) {
        // Update 3D model position back to original (use scene.getObjectByName to avoid stale array)
        const aircraft = sceneRefs.sceneObjects.scene.getObjectByName(allocationId)
        if (aircraft) {
          const newX = reverted.originalPosition[0] * FEET_TO_METERS
          const newY = reverted.originalPosition[1] * FEET_TO_METERS
          const newZ = reverted.originalPosition[2] * FEET_TO_METERS

          aircraft.position.set(newX, newY, newZ)

          // Update rotation ring position to match aircraft
          const rotationRing = sceneRefs.sceneObjects.scene.getObjectByName('rotation-ring')
          if (rotationRing) {
            rotationRing.position.set(newX, 0.2, newZ)  // Keep Y at 0.2 (slightly above ground)
          }

          // Update aircraft rotation
          const data = aircraftData.find((a) => a.id === allocationId)
          if (data) {
            const baseRotation = data.aircraftType.modelSettings.rotation ?? [0, 0, 0]
            const headingAxis = data.aircraftType.modelSettings.headingAxis ?? 'y'
            const headingRad = (reverted.originalRotation * Math.PI) / 180
            const baseRotX = (baseRotation[0] * Math.PI) / 180
            const baseRotY = (baseRotation[1] * Math.PI) / 180
            const baseRotZ = (baseRotation[2] * Math.PI) / 180

            if (headingAxis === 'y') {
              aircraft.rotation.set(baseRotX, baseRotY + headingRad, baseRotZ)
            } else if (headingAxis === 'z') {
              aircraft.rotation.set(baseRotX, baseRotY, baseRotZ + headingRad)
            } else {
              aircraft.rotation.set(baseRotX + headingRad, baseRotY, baseRotZ)
            }
          }

          sceneRefs.map?.triggerRepaint()
        }
        useAircraftEditorStore.getState().setDragging(false)
        toast.info('Position reverted')
      }
    },
    [revertAllocation, sceneRefs, aircraftData]
  )

  // Render overlay — uses filteredAircraftData for filter mode
  const renderOverlay = useCallback(
    (_map: mapboxgl.Map | null, aircraft: PlacedAircraft3D[], loaded3DIds: Set<string>) => {
      // In filter mode, use filtered data; otherwise use full data
      const overlayAircraft = (filterActive && displayMode === 'filter') ? filteredAircraftData : aircraft
      return (
        <>
          {/* Facility name — fades in once the camera pulls back past the
              point where the field itself is legible. */}
          <LocationZoomLabel
            map={_map}
            center={mapCenter ?? null}
            name={locationDisplay.name}
            code={locationDisplay.code}
          />

          {showSpritePreviews && (
            <AircraftSpritesOverlay
              aircraftData={overlayAircraft}
              loaded3DIds={loaded3DIds}
              onFrame={onFrame}
            />
          )}
          <MapLabelsOverlay
            buildings={areas}
            onFrame={onFrame}
          />
          <AircraftLabelsOverlay
            aircraftData={overlayAircraft}
            enrichedData={enrichedData}
            onFrame={onFrame}
          />
          <AircraftOverlay
            aircraftData={overlayAircraft}
            enrichedData={enrichedData}
            buildings={areas}
            onSaveClick={handleSaveClick}
            onRevertClick={handleRevertClick}
            onFrame={onFrame}
          />
        </>
      )
    },
    [enrichedData, areas, handleSaveClick, handleRevertClick, filterActive, displayMode, filteredAircraftData, onFrame, showSpritePreviews, mapCenter, locationDisplay]
  )

  if (!locationId) {
    return (
      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
        Select a location to view ramp
      </div>
    )
  }

  return (
    <div className="relative h-full w-full">
      <MapboxScene
        className="h-full w-full"
        locationId={locationId}
        initialViewSettings={initialViewSettings}
        interactionEnabled={true}
        onReady={handleMapReady}
        renderOverlay={renderOverlay}
      />

      {/*
        Cinema mode: one wrapper blanks every floating control at once.
        A plain (non-positioned) div, so each child keeps positioning itself
        against the outer `relative h-full w-full` — no layout shift.
        The map-projected overlays inside `renderOverlay` are hidden by
        MapboxScene itself; portal-rendered modals escape this wrapper, which is
        fine because they're closed when entering cinema mode.
      */}
      <div
        className="transition-opacity duration-300"
        style={cinemaActive ? { opacity: 0, pointerEvents: 'none' } : undefined}
        aria-hidden={cinemaActive}
      >
      {/* Sidebar toggle - top left */}
      <SidebarToggle />

      {/* Ramp View Config Panel - top right */}
      <RampViewConfigPanel />

      {/* Command Island - top center (search + location) */}
      <CommandIsland
        locationName={locationName || 'Unknown Location'}
        isPlacementMode={isPlacementModeActive}
        minimized={isMinimized}
        onFocusAircraft={handleFocusAircraft}
        onHighlightAircraft={handleHighlightAircraft}
        onFocusArea={handleFocusArea}
      />

      {/* Filter Chips Island - below command island (only when filters active) */}
      <FilterChipsIsland minimized={isMinimized} />

      {/* Action Island - left toolbar */}
      <ActionIsland
        onAddAircraft={handleAddPlane}
        onToggleFilters={handleToggleFilters}
        onOpenPriceCalc={() => setPriceCalcOpen(true)}
        minimized={false}
        arrivalsCount={arrivalsCount}
        demoSlot={isKopf ? (
          <>
            <FlightBridgeSyncButton
              onClick={demo.triggerSync}
              isSyncing={demo.demoState === 'syncing'}
            />
            <DemoResetButton
              onClick={demo.resetDemo}
              isResetting={demo.demoState === 'resetting'}
            />
          </>
        ) : undefined}
      />

      {/* Status Island - bottom center metrics */}
      <StatusIsland
        enrichedData={enrichedData}
        totalUnits={totalUnits}
        minimized={isMinimized}
      />

      {/* Camera tools — bottom right, above the drag/drop toggle.
          Stacking below: MapStyleSelector bottom-4, ViewPresets bottom-14,
          DragDropToggle bottom-24. */}
      <div className="absolute bottom-[8.5rem] right-4 z-20 flex flex-col items-end gap-2">
        {flythroughOpen && (
          <FlythroughPanel
            map={sceneRefs?.map ?? null}
            scope={flythroughScope}
            onClose={() => setFlythroughOpen(false)}
          />
        )}
        <div className="flex items-center gap-1.5">
          <FlythroughButton
            scope={flythroughScope}
            open={flythroughOpen}
            onToggle={() => setFlythroughOpen((v) => !v)}
            compact
          />
          <CinemaToggleButton compact />
        </div>
      </div>

      {/* Drag & Drop Toggle Island - bottom right (above view presets) */}
      <DragDropToggleIsland minimized={isMinimized} />

      {/* View Presets Island - bottom right (saved camera positions) */}
      <ViewPresetsIsland
        locationId={locationId}
        map={sceneRefs?.map ?? null}
        minimized={isMinimized}
      />

      {/* Slide-out Panels */}
      <AircraftListPanel
        enrichedData={enrichedData}
        onFocusAircraft={handleFocusAircraft}
      />
      <SpacesListPanel
        areas={spacesAreas}
        units={spacesUnits}
        enrichedData={enrichedData}
        onFocusArea={handleFocusArea}
      />
      <QuickFitPanel locationId={locationId} onFocusArea={handleFocusArea} />
      <ArrivalsPanel
        enrichedData={enrichedData}
        onFocusAircraft={handleFocusAircraft}
        locationId={locationId}
      />

      {/* Add Plane Wizard Modal */}
      <AddPlaneWizard locationId={locationId} />

      {/* Price Calculator Demo Modal */}
      <PriceCalculatorModal open={priceCalcOpen} onOpenChange={setPriceCalcOpen} />

      {/* 5D Autostacker Demo — KOPF only */}
      {isKopf && (
        <Autostacker5DDemo
          scene={sceneRefs?.sceneObjects?.scene ?? null}
          layerService={sceneRefs?.layerService ?? null}
          demo={demo}
          onPlaceManually={handleAddPlane}
        />
      )}

      {/* Move Bar - viewport-anchored during drag */}
      <MoveBar
        enrichedData={enrichedData}
        buildings={areas}
        onSaveClick={handleSaveClick}
        onRevertClick={handleRevertClick}
      />

      {/* Full Modal - deep work view */}
      <AircraftFullModal enrichedData={enrichedData} />

      {/* Edit Form Container - modal or side panel */}
      <EditFormContainer enrichedData={enrichedData} />

      {/* Collision Debug Panel (dev mode only) */}
      <CollisionDebugPanel
        aircraftCount={aircraftData.length}
        hangarCount={hangarConfigs.length}
        unitCount={unitCollisionConfigs.length}
      />
      </div>
    </div>
  )
}
